import type { GameLoop } from './GameLoop';
import type { PixiHost } from './PixiHost';

/**
 * Keeps the game smooth on slow devices: when the frame rate stays low, the
 * canvas renders at a lower resolution (and, further down, the costly paper
 * shadows and filters are switched off); when it has been smooth for a while
 * it steps back up. Like dynamic resolution in console games.
 */

/** Canvas resolution factor per level (× the device pixel ratio). */
const SCALES = [1, 0.8, 0.65, 0.5] as const;
/** From this level on, CSS shadows/filters are off too (html.perf-lite). */
const LITE_FROM = 2;
const CHECK_MS = 1000;
/** Ignore the first seconds: loading art makes frames slow for a moment. */
const WARMUP_MS = 5000;
/** Seconds in a row below / above the target before stepping. */
const SLOW_CHECKS = 2;
const SMOOTH_CHECKS = 6;
/** The remembered best frame rate fades (half-life ~45 s), so a device that settles at a lower rate is judged by that rate. */
const PEAK_FADE = 0.985;

export class AdaptiveQuality {
  private level = 0;
  private slow = 0;
  private smooth = 0;
  /** Slow again soon after stepping up: stay at least at holdLevel until holdUntil (no flicker). */
  private lastUpAt = -Infinity;
  private holdLevel = 0;
  private holdUntil = 0;
  private readonly started: number;
  /** Best frame rate seen: screens or battery savers that run at 30 are not "slow". */
  private peak = 0;
  /** Scene changes stutter briefly by design; those seconds are not judged. */
  private quietUntil = 0;
  private timer = 0;
  /** Lite mode, once reached, stays on for the session (see set()). */
  private lite = false;

  constructor(
    private readonly loop: Pick<GameLoop, 'stats'>,
    private readonly pixi: Pick<PixiHost, 'setRenderScale'>,
    private readonly target: () => number,
    opts: { now?: number; timer?: boolean } = {},
  ) {
    this.started = opts.now ?? performance.now();
    if (opts.timer !== false) this.timer = window.setInterval(() => this.tick(performance.now()), CHECK_MS);
  }

  get currentLevel(): number {
    return this.level;
  }

  /** Lite mode (no paper overlay, shadows or filters) is on. */
  get liteMode(): boolean {
    return this.lite;
  }

  /** One judgement (every second; tests call it directly). */
  tick(now: number): void {
    if ((typeof document !== 'undefined' && document.hidden) || now - this.started < WARMUP_MS || now < this.quietUntil) return;
    const fps = this.loop.stats.fps;
    if (fps <= 0) return;
    this.peak = Math.max(fps, this.peak * PEAK_FADE);
    const goal = Math.min(this.target(), this.peak);
    if (fps < goal * 0.82) {
      this.smooth = 0;
      if (++this.slow >= SLOW_CHECKS && this.level < SCALES.length - 1) {
        this.set(this.level + 1);
        if (now - this.lastUpAt < 15_000) {
          this.holdLevel = this.level;
          this.holdUntil = now + 60_000;
        }
      }
    } else if (fps >= goal * 0.95) {
      this.slow = 0;
      const held = now < this.holdUntil && this.level - 1 < this.holdLevel;
      if (++this.smooth >= SMOOTH_CHECKS && this.level > 0 && !held) {
        this.lastUpAt = now;
        this.set(this.level - 1);
      }
    } else {
      this.slow = 0;
      this.smooth = 0;
    }
  }

  /** Don't judge the next ms (a scene is being built). */
  pause(ms: number, now = performance.now()): void {
    this.quietUntil = Math.max(this.quietUntil, now + ms);
    this.slow = 0;
  }

  private set(level: number): void {
    this.level = level;
    this.slow = 0;
    this.smooth = 0;
    this.pixi.setRenderScale(SCALES[level]!);
    // Lite mode hides the paper light overlay (a dark vignette): switching it off
    // and on again as frames speed up and slow down flickered like a dark fog, so
    // once the device needed it, it stays for the session. Only resolution steps back up.
    if (level >= LITE_FROM) this.lite = true;
    if (typeof document !== 'undefined') document.documentElement.classList.toggle('perf-lite', this.lite);
  }

  dispose(): void {
    window.clearInterval(this.timer);
  }
}
